import { Plus, Trash2 } from 'lucide-react';
import { FLUID_INPUT_TYPES, FLUID_OUTPUT_TYPES, nowTimeString } from '../constants/flowSheet';
import { reviewCardClass } from './review/reviewStyles';

function FluidEntryCard({ title, children, onRemove }) {
  return (
    <div className={reviewCardClass('normal', 'w-full')}>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-critical" aria-label="Remove entry">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <label className="block text-xs text-gray-600">
      {label}
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block text-xs text-gray-600">
      {label}
      <select
        value={value || options[0]}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function FluidBalanceNote({ note, onChange, allowDelete = true }) {
  const inputs = note.fluid_inputs || [];
  const outputs = note.fluid_outputs || [];

  const setInputs = (fluid_inputs) => onChange({ ...note, fluid_inputs });
  const setOutputs = (fluid_outputs) => onChange({ ...note, fluid_outputs });

  const addInput = () => {
    setInputs([
      ...inputs,
      {
        time_given: nowTimeString(),
        type: FLUID_INPUT_TYPES[0],
        type_other: null,
        volume_ordered_ml: null,
        volume_infused_ml: null,
        rate_ml_hr: null,
        running: true,
      },
    ]);
  };

  const addOutput = () => {
    setOutputs([
      ...outputs,
      {
        time_given: nowTimeString(),
        type: FLUID_OUTPUT_TYPES[0],
        type_other: null,
        volume_ml: null,
      },
    ]);
  };

  const updateInput = (index, updated) => {
    const next = [...inputs];
    next[index] = updated;
    setInputs(next);
  };

  const updateOutput = (index, updated) => {
    const next = [...outputs];
    next[index] = updated;
    setOutputs(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-primary">Fluid Input</h3>
          <button
            type="button"
            onClick={addInput}
            className="flex items-center gap-1 text-xs font-semibold text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Input
          </button>
        </div>
        {inputs.length === 0 ? (
          <p className="text-sm text-gray-500">No fluid input recorded.</p>
        ) : (
          <div className="space-y-2">
            {inputs.map((entry, index) => (
              <FluidEntryCard
                key={`in-${index}`}
                title={`Input ${index + 1}`}
                onRemove={
                  allowDelete
                    ? () => setInputs(inputs.filter((_, i) => i !== index))
                    : undefined
                }
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field
                    label="Time"
                    type="time"
                    value={entry.time_given}
                    onChange={(v) => updateInput(index, { ...entry, time_given: v })}
                  />
                  <SelectField
                    label="Type"
                    value={entry.type}
                    onChange={(v) => updateInput(index, { ...entry, type: v })}
                    options={FLUID_INPUT_TYPES}
                  />
                  {entry.type === 'Other' && (
                    <Field
                      label="Other type"
                      value={entry.type_other}
                      onChange={(v) => updateInput(index, { ...entry, type_other: v })}
                    />
                  )}
                  <Field
                    label="Volume ordered (ml)"
                    type="number"
                    value={entry.volume_ordered_ml}
                    onChange={(v) =>
                      updateInput(index, { ...entry, volume_ordered_ml: v ? Number(v) : null })
                    }
                  />
                  <Field
                    label="Volume infused (ml)"
                    type="number"
                    value={entry.volume_infused_ml}
                    onChange={(v) =>
                      updateInput(index, { ...entry, volume_infused_ml: v ? Number(v) : null })
                    }
                  />
                  <Field
                    label="Rate (ml/hr)"
                    type="number"
                    value={entry.rate_ml_hr}
                    onChange={(v) =>
                      updateInput(index, { ...entry, rate_ml_hr: v ? Number(v) : null })
                    }
                  />
                  <label className="flex items-center gap-2 text-xs text-gray-600 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={entry.running !== false}
                      onChange={(e) => updateInput(index, { ...entry, running: e.target.checked })}
                    />
                    Running
                  </label>
                </div>
              </FluidEntryCard>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-primary">Fluid Output</h3>
          <button
            type="button"
            onClick={addOutput}
            className="flex items-center gap-1 text-xs font-semibold text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Output
          </button>
        </div>
        {outputs.length === 0 ? (
          <p className="text-sm text-gray-500">No fluid output recorded.</p>
        ) : (
          <div className="space-y-2">
            {outputs.map((entry, index) => (
              <FluidEntryCard
                key={`out-${index}`}
                title={`Output ${index + 1}`}
                onRemove={
                  allowDelete
                    ? () => setOutputs(outputs.filter((_, i) => i !== index))
                    : undefined
                }
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field
                    label="Time"
                    type="time"
                    value={entry.time_given}
                    onChange={(v) => updateOutput(index, { ...entry, time_given: v })}
                  />
                  <SelectField
                    label="Type"
                    value={entry.type}
                    onChange={(v) => updateOutput(index, { ...entry, type: v })}
                    options={FLUID_OUTPUT_TYPES}
                  />
                  {entry.type === 'Other' && (
                    <Field
                      label="Other type"
                      value={entry.type_other}
                      onChange={(v) => updateOutput(index, { ...entry, type_other: v })}
                    />
                  )}
                  <Field
                    label="Volume (ml)"
                    type="number"
                    value={entry.volume_ml}
                    onChange={(v) =>
                      updateOutput(index, { ...entry, volume_ml: v ? Number(v) : null })
                    }
                  />
                </div>
              </FluidEntryCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
